import { useMemo, useState, useSyncExternalStore } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Download, LogIn, RefreshCw } from 'lucide-react'
import { cx } from '@/lib/clases'
import { formatearFecha } from '@/lib/fecha'
import { formatearRNC, limpiarRNC } from '@/lib/rnc'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { CampoTexto } from '@/ui/CampoFormulario'
import { FilasEsqueleto } from '@/ui/Esqueleto'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { InsigniaEstado, PuntoEstado } from '@/ui/InsigniaEstado'
import { nombrePeriodo } from '@/dominio/cuenta/tipos'
import { sesionActual, suscribirse } from '@/dominio/sesion/sesion'
import { descargarLote, useCartera, useRegistrarDespacho } from '@/dominio/despacho/useDespacho'
import { TONO_SEMAFORO, type CorteCartera, type FilaCartera, type FormatoCartera } from '@/dominio/despacho/tipos'
import { useCambiarDeSesion } from '@/app/layouts/selectorDeSesion'
import type { Tono } from '@/ui/tonos'
import { EncabezadoPantalla, Tarjeta } from '../-pantalla'
import { ErrorDelDespacho, ErrorEnLinea, mensajeDelServidor } from './-comun'

/**
 * La vista de cartera (`docs/adr/0021` §4, `docs/07` Consola del contador).
 *
 * **Todos los clientes en una pantalla, y lo roto arriba.** Cada fila trae lo
 * que vence en el ciclo —el 10, el 15 y el 20 con su fecha real, los del
 * tablero—, el estado de los formatos 606, 607, 608 y 609 del período, los
 * rechazos de la DGII, los recibidos sin acuse o sin aprobación antes del corte
 * y las contingencias abiertas. El semáforo se explica en una línea.
 *
 * **Clic en el nombre entra al cliente** sin volver a iniciar sesión: se pide
 * otro testigo con el vigente y la base deja la entrada en la auditoría del
 * cliente. Para volver, el mismo selector (`Ctrl+K`), donde el despacho va
 * arriba.
 *
 * Con la sesión de un contribuyente, esta ruta es la puerta: entrar a uno de
 * sus despachos, o registrar el suyo.
 */

export interface BusquedaCartera {
  buscar?: string
  problemas?: boolean
  orden?: 'urgencia' | 'nombre'
}

export const Route = createFileRoute('/despacho/')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaCartera => ({
    buscar: typeof busqueda.buscar === 'string' && busqueda.buscar !== '' ? busqueda.buscar : undefined,
    problemas: busqueda.problemas === true || busqueda.problemas === 'true' ? true : undefined,
    orden: busqueda.orden === 'nombre' ? 'nombre' : undefined,
  }),
  component: PantallaDespacho,
})

function PantallaDespacho() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  return sesion?.despacho ? <PantallaCartera /> : <PuertaDelDespacho />
}

/* ---------------------------------------------------------------------- */
/* La cartera                                                                 */
/* ---------------------------------------------------------------------- */

function PantallaCartera() {
  const { buscar = '', problemas = false, orden = 'urgencia' } = Route.useSearch()
  const navegar = Route.useNavigate()
  const consulta = useCartera()
  const cambio = useCambiarDeSesion()
  const [elegidos, setElegidos] = useState<ReadonlySet<string>>(new Set())
  const [bajando, setBajando] = useState(false)
  const [errorLote, setErrorLote] = useState<unknown>(null)

  const cartera = consulta.data
  const filas = useMemo(() => {
    const texto = buscar.trim().toLowerCase()
    const digitos = limpiarRNC(buscar)
    const todas = (cartera?.filas ?? []).filter(
      (f) =>
        (!problemas || f.semaforo !== 'verde') &&
        (texto === '' ||
          f.razonSocial.toLowerCase().includes(texto) ||
          (f.nombreComercial ?? '').toLowerCase().includes(texto) ||
          (digitos !== '' && f.rnc.includes(digitos))),
    )
    // El servidor ya las manda por urgencia; por nombre se reordena aquí.
    return orden === 'nombre' ? [...todas].sort((a, b) => a.razonSocial.localeCompare(b.razonSocial, 'es')) : todas
  }, [cartera, buscar, problemas, orden])

  const aplicar = (parciales: Partial<BusquedaCartera>): void =>
    void navegar({ search: (previa: BusquedaCartera) => ({ ...previa, ...parciales }), replace: true })

  const alternar = (id: string): void =>
    setElegidos((previos) => {
      const siguiente = new Set(previos)
      if (siguiente.has(id)) siguiente.delete(id)
      else siguiente.add(id)
      return siguiente
    })

  const bajarLote = async (): Promise<void> => {
    if (!cartera) return
    setBajando(true)
    setErrorLote(null)
    try {
      await descargarLote(cartera.periodo, [...elegidos])
    } catch (fallo) {
      setErrorLote(fallo)
    } finally {
      setBajando(false)
    }
  }

  const rojos = (cartera?.filas ?? []).filter((f) => f.semaforo === 'rojo').length
  const ambares = (cartera?.filas ?? []).filter((f) => f.semaforo === 'ambar').length

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Cartera"
        descripcion={
          cartera
            ? `${cartera.filas.length} cliente(s). Se declara ${nombrePeriodo(cartera.periodo)}; ${rojos} en rojo y ${ambares} en ámbar.`
            : 'Todos sus clientes, con lo que vence y lo que está roto en cada uno.'
        }
        acciones={
          <>
            <Boton icono={<RefreshCw />} onClick={() => void consulta.refetch()} disabled={consulta.isFetching}>
              {consulta.isFetching ? 'Actualizando…' : 'Actualizar'}
            </Boton>
            <Boton
              variante="primario"
              icono={<Download />}
              onClick={() => void bajarLote()}
              disabled={!cartera || cartera.filas.length === 0 || bajando}
              title="Un ZIP con los TXT del 606, 607 y 608 —y del 609 si hubo pagos al exterior— de cada cliente"
            >
              {bajando ? 'Armando el ZIP…' : elegidos.size > 0 ? `Bajar formatos (${elegidos.size})` : 'Bajar formatos de todos'}
            </Boton>
          </>
        }
      />

      {errorLote !== null ? <ErrorEnLinea error={errorLote} accion="armar el lote" /> : null}
      {cambio.error !== null ? (
        <Banner tono="rojo" titulo="No se entró al cliente" onCerrar={cambio.limpiarError}>
          {cambio.error}
        </Banner>
      ) : null}
      {cartera?.recortada ? (
        <Banner tono="ambar" titulo="La cartera es más grande de lo que se lee de una vez">
          Se enseñan los primeros 300 clientes por orden alfabético. Escríbale a Agentical Group para ampliar el tope.
        </Banner>
      ) : null}

      {cartera ? <Cortes cortes={cartera.cortes} /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={buscar}
          onChange={(e) => aplicar({ buscar: e.target.value === '' ? undefined : e.target.value })}
          placeholder="Cliente o RNC"
          aria-label="Buscar en la cartera"
          className="h-[var(--alto-control-menor)] w-[260px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
        />
        <label className="flex items-center gap-2 text-cuerpo text-gris-700">
          <input
            type="checkbox"
            checked={problemas}
            onChange={(e) => aplicar({ problemas: e.target.checked ? true : undefined })}
          />
          Solo con problemas
        </label>
        <label className="flex items-center gap-1 text-etiqueta text-gris-600">
          Orden
          <select
            value={orden}
            onChange={(e) => aplicar({ orden: e.target.value === 'nombre' ? 'nombre' : undefined })}
            className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
          >
            <option value="urgencia">Lo más urgente primero</option>
            <option value="nombre">Por nombre</option>
          </select>
        </label>
        {elegidos.size > 0 ? (
          <Boton variante="discreto" tamano="menor" onClick={() => setElegidos(new Set())}>
            Quitar la selección
          </Boton>
        ) : null}
      </div>

      <Tarjeta relleno={false}>
        {consulta.isError ? (
          <ErrorDelDespacho error={consulta.error} queCargaba="la cartera" onReintentar={() => void consulta.refetch()} />
        ) : !consulta.isLoading && (cartera?.filas.length ?? 0) === 0 ? (
          <EstadoVacio
            variante="primera-vez"
            mensaje="Todavía no tiene clientes vinculados. Invítelos por su RNC desde Clientes: cada uno acepta en su portal."
          />
        ) : !consulta.isLoading && filas.length === 0 ? (
          <EstadoVacio variante="sin-resultados" mensaje="Ningún cliente con esos filtros." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-cuerpo" data-densidad="compacta">
              <thead className="bg-gris-50">
                <tr className="border-b border-gris-300 text-left text-etiqueta font-semibold text-gris-600">
                  <th scope="col" className="w-8 px-2">
                    <span className="sr-only">Elegir</span>
                  </th>
                  <th scope="col" className="px-2">Por qué</th>
                  <th scope="col" className="px-2">Cliente</th>
                  <th scope="col" className="px-2">Formatos del período</th>
                  <th scope="col" className="px-2 text-right">Rechazos</th>
                  <th scope="col" className="px-2">Recibidos</th>
                  <th scope="col" className="px-2">Contingencia</th>
                  <th scope="col" className="w-[90px] px-2">
                    <span className="sr-only">Entrar</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading ? (
                  <FilasEsqueleto filas={6} columnas={8} />
                ) : (
                  filas.map((f) => (
                    <FilaDeCartera
                      key={f.tenantId}
                      fila={f}
                      elegida={elegidos.has(f.tenantId)}
                      onElegir={() => alternar(f.tenantId)}
                      onEntrar={() => void cambio.cambiar(f.tenantId)}
                      entrando={cambio.cambiando}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>

      <p className="text-etiqueta text-gris-600">
        La plataforma no presenta nada ante la DGII y no sabe si un formato ya se remitió: «pasado» dice que el día pasó, no
        que se incumplió. Los formatos se suben en la Oficina Virtual.
      </p>
    </div>
  )
}

const TONO_CORTE: Record<CorteCartera['estado'], Tono> = {
  'en-plazo': 'verde',
  proximo: 'ambar',
  'vence-hoy': 'rojo',
  pasado: 'neutral',
}

function Cortes({ cortes }: { cortes: CorteCartera[] }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      {cortes.map((c) => (
        <div key={c.id} className="flex flex-col gap-1 rounded-md border border-gris-300 bg-white p-3">
          <span className="text-etiqueta font-medium uppercase tracking-wide text-gris-600">{c.titulo}</span>
          <span className="flex items-baseline gap-2">
            <span className="numero text-seccion text-gris-900">{formatearFecha(c.fecha)}</span>
            <InsigniaEstado tono={TONO_CORTE[c.estado]}>
              {c.estado === 'pasado'
                ? 'pasó'
                : c.estado === 'vence-hoy'
                  ? 'vence hoy'
                  : `${c.diasRestantes} día${c.diasRestantes === 1 ? '' : 's'}`}
            </InsigniaEstado>
          </span>
          <span className="text-etiqueta text-gris-600">
            {c.descripcion} · período {nombrePeriodo(c.periodo)}
            {c.motivoTraslado ? ` · el ${c.fechaNominal.slice(8, 10)} no es hábil (${c.motivoTraslado})` : ''}
            {c.feriadosProyectados ? ' · feriados proyectados' : ''}
          </span>
        </div>
      ))}
    </div>
  )
}

function FilaDeCartera({
  fila,
  elegida,
  onElegir,
  onEntrar,
  entrando,
}: {
  fila: FilaCartera
  elegida: boolean
  onElegir: () => void
  onEntrar: () => void
  entrando: boolean
}) {
  const primera = fila.senales[0]
  const resto = fila.senales.length - 1

  return (
    <tr className={cx('border-b border-gris-200 align-top last:border-b-0', elegida && 'bg-marca-tenue')}>
      <td className="px-2 py-2">
        <input
          type="checkbox"
          checked={elegida}
          onChange={onElegir}
          aria-label={`Incluir a ${fila.razonSocial} en el lote`}
        />
      </td>
      <td className="max-w-[300px] px-2 py-2" title={fila.senales.map((s) => s.texto).join('\n')}>
        <span className="flex items-start gap-2">
          <PuntoEstado tono={TONO_SEMAFORO[fila.semaforo]} className="mt-[6px]" />
          <span className="flex min-w-0 flex-col">
            <span className="text-gris-900">{primera?.texto ?? 'Nada pendiente antes del próximo corte'}</span>
            {resto > 0 ? <span className="text-etiqueta text-gris-600">y {resto} cosa(s) más</span> : null}
          </span>
        </span>
      </td>
      <td className="px-2 py-2">
        <button
          type="button"
          onClick={onEntrar}
          disabled={entrando}
          className="flex min-w-0 flex-col items-start text-left hover:text-marca disabled:opacity-60"
          title="Entrar al cliente sin volver a iniciar sesión"
        >
          <span className="truncate text-gris-900">{fila.razonSocial}</span>
          <span className="numero text-etiqueta text-gris-600">
            RNC {formatearRNC(fila.rnc)}
            {fila.ambiente !== 'produccion' ? ` · ${fila.ambiente}` : ''}
          </span>
        </button>
        {fila.asignados && fila.asignados.length > 0 ? (
          <span className="text-etiqueta text-gris-600">Asignado a {fila.asignados.join(', ')}</span>
        ) : null}
      </td>
      <td className="px-2 py-2">
        {fila.leida ? (
          <span className="flex flex-wrap gap-1">
            {fila.formatos.filter((f) => f.aplica).map((f) => (
              <ChipFormato key={f.formato} formato={f} />
            ))}
          </span>
        ) : (
          <span className="text-etiqueta text-ambar">{fila.fallo}</span>
        )}
      </td>
      <td className="numero px-2 py-2 text-right">
        {fila.rechazos ? (
          <span
            className={cx(fila.rechazos.terminales + fila.rechazos.corregibles > 0 ? 'text-rojo' : 'text-gris-600')}
            title={`${fila.rechazos.terminales} con la secuencia consumida, ${fila.rechazos.corregibles} corregibles, del ${formatearFecha(fila.rechazos.desde)} al ${formatearFecha(fila.rechazos.hasta)}`}
          >
            {fila.rechazos.terminales + fila.rechazos.corregibles}
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-2 py-2 text-etiqueta">
        {fila.recibidos ? (
          <span className="flex flex-col">
            <span className={fila.recibidos.sinAcuse > 0 ? 'text-ambar' : 'text-gris-600'}>
              {fila.recibidos.sinAcuse} sin acuse
            </span>
            <span className="text-gris-600">
              {fila.recibidos.sinAprobacion} sin aprobación
              {fila.recibidos.diasParaElCorte !== null && fila.recibidos.sinAprobacion > 0
                ? ` · corte en ${fila.recibidos.diasParaElCorte} d`
                : ''}
            </span>
          </span>
        ) : (
          '—'
        )}
      </td>
      <td className="px-2 py-2 text-etiqueta">
        {fila.contingencias ? (
          fila.contingencias.abiertas > 0 ? (
            <span className="text-rojo">
              {fila.contingencias.abiertas} abierta(s)
              {fila.contingencias.limiteTransmision ? ` · transmitir antes del ${formatearFecha(fila.contingencias.limiteTransmision)}` : ''}
            </span>
          ) : fila.contingencias.dgiiCaida ? (
            <span className="text-ambar">DGII caída</span>
          ) : (
            <span className="text-gris-600">Ninguna</span>
          )
        ) : (
          '—'
        )}
      </td>
      <td className="px-2 py-2">
        <Boton tamano="menor" icono={<LogIn />} onClick={onEntrar} disabled={entrando}>
          Entrar
        </Boton>
      </td>
    </tr>
  )
}

function ChipFormato({ formato }: { formato: FormatoCartera }) {
  const tono: Tono = formato.errores > 0 ? 'rojo' : formato.avisos > 0 ? 'ambar' : 'verde'
  const detalle = `${formato.formato}: ${formato.documentos} registro(s), ${formato.errores} error(es), ${formato.avisos} aviso(s)${
    formato.sinAprobacion > 0 ? `, ${formato.sinAprobacion} sin aprobación` : ''
  }`

  return (
    <span
      title={detalle}
      className="inline-flex items-center gap-1 rounded border border-gris-300 bg-white px-1.5 py-0.5 text-etiqueta text-gris-900"
    >
      <PuntoEstado tono={tono} />
      <span className="numero">{formato.formato}</span>
      {formato.errores > 0 ? <span className="numero text-rojo">{formato.errores}</span> : null}
    </span>
  )
}

/* ---------------------------------------------------------------------- */
/* La puerta, con la sesión de un contribuyente                               */
/* ---------------------------------------------------------------------- */

function PuertaDelDespacho() {
  const sesion = useSyncExternalStore(suscribirse, sesionActual, () => null)
  const cambio = useCambiarDeSesion()
  const registrar = useRegistrarDespacho()
  const [nombre, setNombre] = useState('')
  const [identificacion, setIdentificacion] = useState('')
  const [correo, setCorreo] = useState('')

  const digitos = limpiarRNC(identificacion)
  const listo = nombre.trim().length >= 3 && (digitos.length === 9 || digitos.length === 11) && correo.includes('@')

  const enviar = (evento: React.FormEvent): void => {
    evento.preventDefault()
    registrar.mutate(
      { nombre: nombre.trim(), identificacion: digitos, correo: correo.trim() },
      { onSuccess: (r) => void cambio.cambiar(`despacho:${r.despacho}`) },
    )
  }

  const despachos = sesion?.despachos ?? []

  return (
    <div className="flex max-w-[760px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Despacho contable"
        descripcion="Si lleva la contabilidad de varios contribuyentes, su despacho le da todos sus clientes en una pantalla y el salto entre ellos sin volver a iniciar sesión. Cada cliente lo acepta en su portal y lo puede revocar cuando quiera."
      />

      {cambio.error !== null ? (
        <Banner tono="rojo" titulo="No se entró al despacho" onCerrar={cambio.limpiarError}>
          {cambio.error}
        </Banner>
      ) : null}

      {despachos.length > 0 ? (
        <Tarjeta titulo="Sus despachos">
          <ul className="flex flex-col gap-2">
            {despachos.map((d) => (
              <li key={d.despacho} className="flex items-center justify-between gap-3">
                <span className="flex flex-col">
                  <span className="text-gris-900">{d.nombre}</span>
                  <span className="numero text-etiqueta text-gris-600">
                    {formatearRNC(d.identificacion)} · {d.rol}
                    {d.verificado ? ' · verificado por Agentical Group' : ' · sin verificar'}
                  </span>
                </span>
                <Boton
                  variante="primario"
                  icono={<LogIn />}
                  disabled={cambio.cambiando}
                  onClick={() => void cambio.cambiar(`despacho:${d.despacho}`)}
                >
                  Entrar a la cartera
                </Boton>
              </li>
            ))}
          </ul>
        </Tarjeta>
      ) : null}

      <Tarjeta titulo="Registrar su despacho">
        <form onSubmit={enviar} className="flex flex-col gap-4">
          <p className="text-cuerpo text-gris-600">
            Usted queda de titular. El despacho nace <strong>sin verificar</strong>: sus clientes lo ven así en la
            invitación hasta que Agentical Group compruebe el RNC.
          </p>
          <CampoTexto etiqueta="Nombre del despacho" obligatorio value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <CampoTexto
            etiqueta="RNC del despacho, o su cédula si es independiente"
            obligatorio
            numerico
            inputMode="numeric"
            value={identificacion}
            onChange={(e) => setIdentificacion(e.target.value)}
            error={
              identificacion !== '' && digitos.length !== 9 && digitos.length !== 11
                ? 'Nueve dígitos el RNC, once la cédula.'
                : undefined
            }
          />
          <CampoTexto
            etiqueta="Correo del despacho"
            obligatorio
            type="email"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
          />
          {registrar.isError ? (
            <p role="alert" className="text-etiqueta text-rojo">
              {mensajeDelServidor(registrar.error) ?? 'No se pudo registrar el despacho.'}
            </p>
          ) : null}
          <div>
            <Boton type="submit" variante="primario" disabled={!listo || registrar.isPending || cambio.cambiando}>
              {registrar.isPending ? 'Registrando…' : 'Registrar y entrar'}
            </Boton>
          </div>
        </form>
      </Tarjeta>
    </div>
  )
}
